import { useState, useEffect } from "react";
import {
  ArrowLeft,
  Target,
  Calendar,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import homeService, { HomePageData } from "../../services/HomeService";

const BiteBalance = () => {
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // Use React Query to fetch and cache home page data
  // Force refetch on mount to get latest scores
  const {
    data: homeData,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
    staleTime: 0, // Always consider data stale
    refetchOnMount: true, // Refetch when component mounts
  });

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isToday = (date: Date | null) => {
    if (!date) {
      return false;
    }
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date | null) => {
    if (!date || !selectedDate) {
      return false;
    }
    return date.toDateString() === selectedDate.toDateString();
  };

  // Get score classification based on total score
  const getScoreClassification = (score: number) => {
    if (score >= 80) {
      return { label: "Excellent", color: "text-green-600" };
    }
    if (score >= 60) {
      return { label: "Good", color: "text-blue-600" };
    }
    if (score >= 40) {
      return { label: "Fair", color: "text-yellow-600" };
    }
    return { label: "Needs Improvement", color: "text-red-600" };
  };

  const gutHealthScore = homeData?.["Gut Health Score"] || 0;
  const foodScore = homeData?.["Food Score"] || 0;
  const lifestyleScore = homeData?.["Lifestyle Score"] || 0;
  const classification = getScoreClassification(gutHealthScore);

  // Show loading state
  if (loading) {
    return (
      <div
        className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
        style={{
          minHeight: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-10 w-10 rounded-xl border-border bg-card"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground">
                Gut Health Score
              </h1>
              <p className="text-sm text-muted-foreground">
                {selectedDate?.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
          <div className="h-10 w-10" />
        </div>

        {/* Gut Health Score Card */}
        <Card className="rounded-xl border-2 border-border bg-card p-6 shadow-md">
          <h2 className="mb-1 text-lg font-semibold text-foreground">
            Your Gut Health Score
          </h2>
          <p className="mb-6 text-xs text-muted-foreground">
            Comprehensive digestive wellness analysis
          </p>

          {/* Main Gut Health Score */}
          <div className="mb-4 flex flex-col items-center justify-center py-4">
            <div className="mb-2 text-5xl font-bold text-orange-500">
              {gutHealthScore}
            </div>
            <p className="text-sm text-muted-foreground">out of 100</p>
            <p className={`mt-2 text-sm font-semibold ${classification.color}`}>
              {classification.label}
            </p>
          </div>

          {/* Food Score and Lifestyle Score - Side by Side */}
          <div className="mb-6 grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-4 dark:border-blue-800 dark:from-blue-950 dark:to-blue-900">
              <div className="mb-2 text-center text-xs text-muted-foreground">
                Food Score
              </div>
              <div className="text-center text-3xl font-bold text-blue-600 dark:text-blue-400">
                {foodScore}
                <span className="text-sm text-muted-foreground">/60</span>
              </div>
            </div>
            <div className="rounded-lg border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-4 dark:border-green-800 dark:from-green-950 dark:to-green-900">
              <div className="mb-2 text-center text-xs text-muted-foreground">
                Lifestyle Score
              </div>
              <div className="text-center text-3xl font-bold text-green-600 dark:text-green-400">
                {lifestyleScore}
                <span className="text-sm text-muted-foreground">/40</span>
              </div>
            </div>
          </div>
        </Card>

        {/* What is Gut Health Score */}
        <Card className="rounded-xl border-2 border-border bg-gradient-to-br from-orange-50 to-orange-100 p-6 shadow-md dark:from-orange-950 dark:to-orange-900">
          <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground">
            <Target className="h-5 w-5 text-orange-500" />
            What is Gut Health Score?
          </h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              The{" "}
              <span className="font-semibold text-foreground">
                Gut Health Score
              </span>{" "}
              is a comprehensive metric that evaluates your digestive wellness
              through two key components:
            </p>
            <div className="space-y-3 rounded-lg bg-muted/60 p-4">
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                  Food Score (0-60 points)
                </h4>
                <p className="text-xs">
                  Evaluates the nutritional quality of your meals, including
                  micronutrient diversity, fiber content, anti-inflammatory
                  properties, and prebiotic richness.
                </p>
              </div>
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground">
                  Lifestyle Score (0-40 points)
                </h4>
                <p className="text-xs">
                  Assesses lifestyle factors like sleep quality, stress
                  management, physical activity, and meal timing that impact gut
                  health.
                </p>
              </div>
            </div>
            <p className="text-xs">
              <span className="font-semibold text-foreground">
                Combined Score:
              </span>{" "}
              Your total Gut Health Score (0-100) reflects the synergy between
              what you eat and how you live, providing actionable insights for
              optimal digestive wellness.
            </p>
          </div>
        </Card>

        {/* Score Ranges */}
        <Card className="rounded-xl border-2 border-border bg-card p-6 shadow-md">
          <h3 className="mb-4 text-lg font-bold text-foreground">
            Score Ranges
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                Excellent
              </span>
              <span className="text-sm font-semibold text-green-600">
                80-100
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Good</span>
              <span className="text-sm font-semibold text-blue-600">60-79</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Fair</span>
              <span className="text-sm font-semibold text-yellow-600">
                40-59
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                Needs Improvement
              </span>
              <span className="text-sm font-semibold text-red-600">0-39</span>
            </div>
          </div>
        </Card>
      </div>

      <BottomNavBar activeIndex={0} />
    </div>
  );
};

export default BiteBalance;
